import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'providers/AllProviders';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AddColumn,
  AddRow,
  appendCsv,
  createWorksheet,
  deleteColumn,
  deleteRow,
  deleteWorksheet,
  exucuteWorkflow,
  getColumns,
  getFilteredRows,
  getWorkflow,
  getWorksheet,
  getWorksheets,
  UpdateCell,
  UpdateColumn,
  UpdateRow,
  updateWorksheet
} from './api';
import {
  AddColumnRequest,
  AddRowRequest,
  AppendCsv,
  ColumnResponse,
  RowsRequest,
  UpdateCellRequest,
  WorksheetRequest,
  WorksheetResponse
} from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import * as urls from 'routes/config';

const ROWS_PER_PAGE = 50;

export function useGetWorksheets(params?: Record<string, any>) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<{ data: WorksheetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['worksheets', locationId, agentId, params],
    queryFn: () => getWorksheets(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId)
  });
}

export function useGetWorksheet(params?: Record<string, any>) {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useQuery<WorksheetResponse>({
    queryKey: ['worksheet', locationId, agentId, worksheetId, params],
    queryFn: () => getWorksheet(locationId!, agentId!, worksheetId!, params),
    enabled: Boolean(locationId && agentId && worksheetId)
  });
}

export function useGetColumns(params?: any) {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useQuery<{ data: ColumnResponse[]; meta: MetaApiResponse }>({
    queryKey: ['columns', locationId, agentId, worksheetId, params],
    queryFn: () => getColumns(locationId!, agentId!, worksheetId!, params),
    enabled: Boolean(locationId && agentId && worksheetId)
  });
}

export function useGetInfiniteRows(
  filters?: Record<string, any>,
  sorting?: Record<string, any>,
  perPage = ROWS_PER_PAGE
) {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  const queryParams = { ...filters, ...sorting, per_page: perPage };

  return useInfiniteQuery<{ data: any[]; meta: MetaApiResponse }>({
    queryKey: ['rows', locationId, agentId, worksheetId, queryParams],
    queryFn: ({ pageParam }) =>
      getFilteredRows(locationId!, agentId!, worksheetId!, {
        ...queryParams,
        page: pageParam
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = lastPage.meta?.total_pages ?? 0;
      if (allPages.length >= totalPages) return undefined;
      return allPages.length + 1;
    },
    enabled: Boolean(locationId && agentId && worksheetId),
    refetchInterval: (query) => {
      if (!query.state.data) return false;
      if (query.state.fetchStatus === 'fetching') return false;
      return 5000;
    },
    refetchIntervalInBackground: false
  });
}

export function useCreateWorksheet() {
  const navigate = useNavigate();
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();

  return useMutation({
    mutationFn: () => createWorksheet(locationId!, agentId!),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        navigate(`${urls.worksheetBase(locationId, agentId)}/${data?.id}`);
      }
    }
  });
}

export function useUpdateWorksheet() {
  const navigate = useNavigate();
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: WorksheetRequest) => updateWorksheet(locationId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        navigate(-1);
      }
    }
  });
}

export function useAddColumn(worksheet?: string) {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (payload: AddColumnRequest) =>
      AddColumn(locationId!, agentId!, worksheetId! ?? worksheet ?? payload.id, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useAddRow() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: AddRowRequest) => AddRow(locationId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['rows', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateCell() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ rowId, columnId, payload }: { rowId: string; columnId: number; payload: UpdateCellRequest }) =>
      UpdateCell(locationId!, agentId!, worksheetId!, rowId, columnId, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['rows', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateColumn() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ id, columnId, payload }: { id?: string; columnId: string; payload: object }) =>
      UpdateColumn(locationId!, agentId!, worksheetId! ?? id, columnId, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useUpdateRow() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: ({ rowId, payload }: { rowId: string; payload: object }) =>
      UpdateRow(locationId!, agentId!, worksheetId!, rowId, payload)
  });
}

export function useDeleteAgentWorksheet() {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (worksheetId: string) => deleteWorksheet(locationId!, agentId!, worksheetId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['worksheets', locationId, agentId],
        refetchType: 'all'
      });
      navigate(`${urls.worksheetBase(locationId, agentId)}`);
    }
  });
}

export function useDeleteColumn() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (columnId: string) => deleteColumn(locationId!, agentId!, worksheetId!, columnId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['columns', locationId, agentId, worksheetId],
        refetchType: 'all'
      });
    }
  });
}

export function useDeleteRow() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();
  return useMutation({
    mutationFn: (payload: RowsRequest) => deleteRow(locationId!, agentId!, worksheetId!, payload!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['columns', locationId, agentId, worksheetId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['worksheet', locationId, agentId, worksheetId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['worksheets', locationId, agentId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['rows', locationId, agentId, worksheetId],
        refetchType: 'all'
      });
    }
  });
}

export function useExecuteWorkflow() {
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: { row_id?: number | number[]; column_id: number; limit: string }) =>
      exucuteWorkflow(locationId!, agentId!, worksheetId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['columns', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheets', locationId, agentId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['worksheet', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: ['rows', locationId, agentId, worksheetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useCsvAppend() {
  const navigate = useNavigate();
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
    appendCsv: string;
  }>();
  return useMutation({
    mutationFn: (payload: AppendCsv) => appendCsv(locationId!, agentId!, worksheetId!, payload),
    onSuccess: () => {
      navigate(-1);
    }
  });
}

export function useGetWorkflow() {
  const { locationId } = useParams<{ locationId: string }>();

  return useQuery<any>({
    queryKey: ['workflows', locationId],
    queryFn: () => getWorkflow(locationId!),
    enabled: Boolean(locationId)
  });
}
